import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../utils/constants';
import apiService from '../services/api';
import CourseCard from '../components/courses/CourseCard';
import StorybookCard from '../components/storybooks/StorybookCard';
import SubscriptionStatus, { SubscriptionCompactBadge } from '../components/dashboard/SubscriptionStatus';

const DashboardPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [purchasedContent, setPurchasedContent] = useState([]);
  const [manualPayments, setManualPayments] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [stats, setStats] = useState({
    totalCourses: 0,
    averageProgress: 0,
    completedCourses: 0,
  });
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      navigate(ROUTES.LOGIN);
    } else if (user) {
      loadUserData();
    }
  }, [user, loading, navigate]);

  const loadUserData = async () => {
    try {
      setLoadingData(true);
      const purchases = await apiService.getUserPurchases();
      setPurchasedContent(purchases);
      
      // Keep backward compatibility - filter only courses for stats
      const courses = purchases.filter(p => p.content_type === 'course');
      
      // Load subscription status (will implement API later)
      try {
        const subStatus = await apiService.getSubscriptionStatus();
        setSubscriptionStatus(subStatus);
      } catch (err) {
        console.log('No active subscription');
      }
      
      // Load bundles (will implement API later)
      try {
        const userBundles = await apiService.getUserBundles();
        setBundles(userBundles);
      } catch (err) {
        console.log('No bundles found');
      }
      
      // Load manual payments
      try {
        const payments = await apiService.getUserManualPayments();
        setManualPayments(payments);
      } catch (err) {
        console.error('Error loading manual payments:', err);
      }

      // Load unread notifications count
      try {
        const readNotificationIds = JSON.parse(localStorage.getItem('readNotifications') || '[]');
        const generatedNotifications = [];
        
        purchases.forEach((purchase) => {
          const notifId = `purchase-${purchase.id}`;
          generatedNotifications.push({
            id: notifId,
            read: readNotificationIds.includes(notifId)
          });
        });

        try {
          const payments = await apiService.getUserManualPayments();
          payments.forEach((payment) => {
            if (payment.status === 'approved' || payment.status === 'rejected') {
              const notifId = `payment-${payment.status}-${payment.id}`;
              generatedNotifications.push({
                id: notifId,
                read: readNotificationIds.includes(notifId)
              });
            }
          });
        } catch (err) {
          console.error('Error loading payments for notifications:', err);
        }

        const unread = generatedNotifications.filter(n => !n.read).length;
        setUnreadCount(unread);
      } catch (err) {
        console.error('Error loading unread count:', err);
      }
      
      const totalCourses = purchases.length; // Count ALL content (courses + storybooks)
      const completedCourses = courses.filter(c => c.completed).length;
      
      let totalProgress = 0;
      courses.forEach(course => {
        if (course.pages_count && course.current_page) {
          totalProgress += (course.current_page / course.pages_count) * 100;
        }
      });
      const averageProgress = totalCourses > 0 ? Math.round(totalProgress / totalCourses) : 0;
      
      setStats({
        totalCourses,
        averageProgress,
        completedCourses,
      });
    } catch (err) {
      console.error('Error loading user data:', err);
    } finally {
      setLoadingData(false);
    }
  };

  // Function to render purchase type badge
  const renderPurchaseTypeBadge = (item) => {
    if (!item.purchase_type) {
      return (
        <div className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
          <i className="fas fa-shopping-cart"></i>
          <span>مشترى منفرد</span>
        </div>
      );
    }

    switch (item.purchase_type) {
      case 'subscription':
        // Calculate days remaining if subscription end date exists
        let daysRemainingText = '';
        if (item.subscription_end_date) {
          const endDate = new Date(item.subscription_end_date);
          const today = new Date();
          const daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
          daysRemainingText = daysRemaining > 0 ? `(${daysRemaining} يوماً متبقية)` : '(منتهي)';
        } else {
          daysRemainingText = '(مؤقت)';
        }
        
        return (
          <div className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
            <i className="fas fa-calendar-check"></i>
            <span>الاشتراك الشهري</span>
            <span className="text-orange-600">{daysRemainingText}</span>
          </div>
        );
      case 'bundle':
        return (
          <div className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">
            <i className="fas fa-gift"></i>
            <span>ضمن الحزمة #{item.bundle_id || '?'}</span>
            <span className="text-green-600">(دائم)</span>
          </div>
        );
      case 'individual':
      default:
        return (
          <div className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
            <i className="fas fa-shopping-cart"></i>
            <span>مشترى منفرد</span>
            <span className="text-green-600">(دائم)</span>
          </div>
        );
    }
  };

  if (loading || loadingData) {
    return (
      <div className="relative z-10 min-h-screen flex items-center justify-center bg-bg">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text3">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="relative z-10 py-12 md:py-20 pb-24 md:pb-20 bg-gradient-to-b from-bg to-white min-h-screen overflow-x-hidden">
      <div className="container mx-auto px-3 md:px-4">
        <div className="max-w-[1400px] mx-auto">
          {/* Header */}
          <div className="mb-8 md:mb-12">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 mb-3 md:mb-4">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-black text-text">أهلا وسهلا {user.name}</h1>
                <SubscriptionCompactBadge />
              </div>
            </div>
            <p className="text-sm md:text-lg text-text3">هذي لوحة التحكم تاعك، شوف تقدمك واستمر في التعلم</p>
          </div>

          {/* Subscription Status Component */}
          {/* Removed - now shown in header badge */}

          {/* Quick Navigation Tabs - 4 cards (2x2 on mobile) */}
          <div className="mb-8 md:mb-12 bg-white rounded-xl md:rounded-2xl shadow-custom p-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Link 
                to={ROUTES.MY_CONTENT}
                className="px-3 md:px-6 py-3 md:py-4 bg-white hover:bg-gray-50 text-primary border-2 border-primary rounded-lg md:rounded-xl font-bold transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div className="flex flex-col md:flex-row items-center md:gap-3 text-center md:text-right">
                    <i className="fas fa-book text-xl md:text-2xl"></i>
                    <span className="text-xs md:text-lg">محتواي التعليمي</span>
                  </div>
                  <span className="bg-primary text-white px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm">{purchasedContent.length}</span>
                </div>
              </Link>
              
              <Link 
                to={ROUTES.MANUAL_PAYMENTS}
                className="px-3 md:px-6 py-3 md:py-4 bg-white hover:bg-orange-50 text-orange-600 border-2 border-orange-400 rounded-lg md:rounded-xl font-bold transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div className="flex flex-col md:flex-row items-center md:gap-3 text-center md:text-right">
                    <i className="fas fa-clock text-xl md:text-2xl"></i>
                    <span className="text-xs md:text-lg">طلبات قيد المراجعة</span>
                  </div>
                  <span className="bg-orange-500 text-white px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-bold">
                    {manualPayments.filter(p => p.status === 'pending').length}
                  </span>
                </div>
              </Link>

              <Link 
                to={ROUTES.BUNDLES}
                className="px-3 md:px-6 py-3 md:py-4 bg-white hover:bg-purple-50 text-purple-600 border-2 border-purple-400 rounded-lg md:rounded-xl font-bold transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div className="flex flex-col md:flex-row items-center md:gap-3 text-center md:text-right">
                    <i className="fas fa-boxes text-xl md:text-2xl"></i>
                    <span className="text-xs md:text-lg">حزمي المشتراة</span>
                  </div>
                  {bundles.length > 0 && (
                    <span className="bg-purple-500 text-white px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-bold">
                      {bundles.length}
                    </span>
                  )}
                </div>
              </Link>

              <Link 
                to={ROUTES.PURCHASES}
                className="px-3 md:px-6 py-3 md:py-4 bg-white hover:bg-gray-50 text-primary border-2 border-primary rounded-lg md:rounded-xl font-bold transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div className="flex flex-col md:flex-row items-center md:gap-3 text-center md:text-right">
                    <i className="fas fa-shopping-bag text-xl md:text-2xl"></i>
                    <span className="text-xs md:text-lg">سجل المشتريات</span>
                  </div>
                </div>
              </Link>
            </div>
          </div>

          {/* Subscription Status Card - Only show if active */}
          {subscriptionStatus && subscriptionStatus.is_active && (
            <div className="mb-8 md:mb-12">
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-2xl md:rounded-3xl p-4 md:p-6 lg:p-8 shadow-custom-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 md:w-56 md:h-56 lg:w-64 lg:h-64 bg-white/10 rounded-full blur-3xl"></div>
                <div className="relative z-10">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0 mb-4">
                    <div className="flex items-center gap-3">
                      <i className="fas fa-calendar-check text-2xl md:text-4xl"></i>
                      <div>
                        <h3 className="text-lg md:text-2xl font-black">اشتراكك الشهري</h3>
                        <p className="text-sm md:text-base text-white/90">4900 دج/شهر</p>
                      </div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm px-3 md:px-4 py-1.5 md:py-2 rounded-lg md:rounded-xl self-start md:self-auto">
                      <span className="text-sm md:text-base font-bold">نشط ✅</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mt-4 md:mt-6">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg md:rounded-xl p-3 md:p-4">
                      <div className="text-xs md:text-sm text-white/80 mb-1">التجديد التالي</div>
                      <div className="text-base md:text-xl font-bold">{new Date(subscriptionStatus.next_payment_date).toLocaleDateString('ar-DZ')}</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg md:rounded-xl p-3 md:p-4">
                      <div className="text-xs md:text-sm text-white/80 mb-1">الوصول</div>
                      <div className="text-base md:text-xl font-bold">جميع المحتوى (20+)</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stats Cards - Hidden on mobile to avoid duplication with tabs */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-white rounded-2xl p-8 shadow-custom hover:shadow-custom-lg transition-all duration-300">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-primary to-primary-light rounded-xl flex items-center justify-center text-white text-xl">
                  <i className="fas fa-book"></i>
                </div>
                <div>
                  <div className="text-3xl font-black text-primary">{stats.totalCourses}</div>
                  <div className="text-sm text-text3">محتوى مشترى</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-custom hover:shadow-custom-lg transition-all duration-300">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-primary to-primary-light rounded-xl flex items-center justify-center text-white text-xl">
                  <i className="fas fa-chart-line"></i>
                </div>
                <div>
                  <div className="text-3xl font-black text-primary">{stats.averageProgress}%</div>
                  <div className="text-sm text-text3">التقدم الكلي</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-custom hover:shadow-custom-lg transition-all duration-300">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-primary to-primary-light rounded-xl flex items-center justify-center text-white text-xl">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div>
                  <div className="text-3xl font-black text-primary">{stats.completedCourses}</div>
                  <div className="text-sm text-text3">محتوى مكتمل</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-8 shadow-custom hover:shadow-custom-lg transition-all duration-300">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center text-white text-xl">
                  <i className="fas fa-piggy-bank"></i>
                </div>
                <div>
                  <div className="text-3xl font-black text-white">
                    {bundles.reduce((sum, b) => sum + (parseFloat(b.discount_amount) || 0), 0).toFixed(0)} دج
                  </div>
                  <div className="text-sm text-white/90 font-semibold">التوفير الإجمالي من الحزم</div>
                </div>
              </div>
            </div>
          </div>

          {/* Manual Payment Requests Section - Only show PENDING requests */}
          {manualPayments.filter(p => p.status === 'pending').length > 0 && (
            <div id="pending-payments-section" className="mb-8 md:mb-12">
              <details className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl md:rounded-3xl overflow-hidden">
                <summary className="cursor-pointer p-4 md:p-6 font-black text-lg md:text-2xl text-text hover:bg-yellow-100 transition-colors flex items-center justify-between">
                  <span>طلبات الدفع قيد المراجعة ({manualPayments.filter(p => p.status === 'pending').length})</span>
                  <i className="fas fa-chevron-down"></i>
                </summary>
                <div className="p-4 md:p-6 pt-0 space-y-3 md:space-y-4">
                  {manualPayments.filter(p => p.status === 'pending').map((payment) => (
                    <div 
                      key={payment.id}
                      className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-custom hover:shadow-custom-lg transition-all duration-300"
                    >
                      <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-start md:items-center justify-between">
                        <div className="flex items-start md:items-center gap-3 md:gap-4 flex-1 w-full md:w-auto">
                          {payment.cover_image && (
                            <img 
                              src={payment.cover_image} 
                              alt={payment.course_title}
                              className="w-12 h-12 md:w-16 md:h-16 object-cover rounded-lg flex-shrink-0"
                            />
                          )}
                          <div className="flex-1">
                            {payment.purchase_type === 'bundle' ? (
                              <h3 className="font-bold text-sm md:text-base text-text mb-1 flex items-center gap-2">
                                <i className="fas fa-boxes text-purple-600 text-sm md:text-base"></i>
                                حزمة مخصصة ({payment.item_ids ? JSON.parse(payment.item_ids).length : 0} محتويات)
                              </h3>
                            ) : payment.purchase_type === 'subscription' ? (
                              <h3 className="font-bold text-sm md:text-base text-text mb-1 flex items-center gap-2">
                                <i className="fas fa-calendar-check text-green-600 text-sm md:text-base"></i>
                                الاشتراك الشهري
                              </h3>
                            ) : (
                              <h3 className="font-bold text-sm md:text-base text-text mb-1">{payment.course_title}</h3>
                            )}
                            <p className="text-xs md:text-sm text-text3">المبلغ: {payment.amount} دج</p>
                            <p className="text-[10px] md:text-xs text-text3">
                              تاريخ الطلب: {new Date(payment.created_at).toLocaleDateString('ar-DZ')}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 md:gap-3 self-start md:self-auto">
                          <div className="px-3 md:px-4 py-1.5 md:py-2 rounded-lg md:rounded-xl font-bold text-xs md:text-sm bg-yellow-100 text-yellow-700">
                            قيد المراجعة
                          </div>
                        </div>
                      </div>
                      
                      {payment.admin_notes && (
                        <div className="mt-3 md:mt-4 p-2 md:p-3 bg-blue-50 rounded-lg border-r-4 border-blue-500">
                          <p className="text-xs md:text-sm font-bold text-blue-900 mb-1">ملاحظة الإدارة:</p>
                          <p className="text-xs md:text-sm text-blue-800">{payment.admin_notes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}

          {purchasedContent.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-6 md:mb-8">
                <h2 className="text-xl md:text-3xl font-black text-text">محتواي التعليمي</h2>
                {purchasedContent.length > 9 && (
                  <Link
                    to={ROUTES.MY_CONTENT}
                    className="inline-flex items-center gap-1 md:gap-2 px-3 md:px-6 py-2 md:py-3 bg-gradient-to-r from-primary to-primary-dark text-white rounded-lg md:rounded-xl font-bold hover:opacity-90 transition-opacity shadow-lg text-xs md:text-base"
                  >
                    <i className="fas fa-th-large text-sm md:text-base"></i>
                    <span className="hidden sm:inline">عرض الكل ({purchasedContent.length})</span>
                    <span className="sm:hidden">الكل</span>
                  </Link>
                )}
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-8">
                {purchasedContent.slice(0, 9).map(item => (
                  <div key={`${item.content_type}-${item.id}`}>
                    {/* Purchase Type Badge - Above the card */}
                    <div className="mb-2">
                      {renderPurchaseTypeBadge(item)}
                    </div>
                    
                    {item.content_type === 'storybook' ? (
                      <StorybookCard 
                        storybook={item} 
                        isPurchased={true}
                      />
                    ) : (
                      <CourseCard 
                        course={item} 
                        isPurchased={true}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Show message if there are more items */}
              {purchasedContent.length > 9 && (
                <div className="mt-6 md:mt-8 text-center">
                  <Link
                    to={ROUTES.MY_CONTENT}
                    className="inline-flex items-center gap-2 px-6 md:px-8 py-3 md:py-4 bg-white border-2 border-primary text-primary rounded-xl font-bold hover:bg-primary hover:text-white transition-all duration-300 shadow-custom text-sm md:text-base"
                  >
                    <i className="fas fa-eye"></i>
                    <span>عرض المحتوى المتبقي ({purchasedContent.length - 9} محتوى)</span>
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gradient-to-br from-primary to-primary-dark text-white rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-12 text-center shadow-custom-lg">
              <i className="fas fa-book-open text-4xl md:text-6xl mb-4 md:mb-6 opacity-90"></i>
              <h2 className="text-xl md:text-3xl font-black mb-3 md:mb-4">ابدأ رحلتك التعليمية درك!</h2>
              <p className="text-sm md:text-lg mb-6 md:mb-8 opacity-90 max-w-[600px] mx-auto">
                باقي ما شريت والو. شوف المكتبة الرقمية تاعنا وابدأ التعلم!
              </p>
              <Link
                to={ROUTES.COURSES}
                className="inline-block px-6 md:px-8 py-3 md:py-4 bg-white text-primary font-bold rounded-xl hover:bg-white/90 transition-all shadow-lg text-sm md:text-base"
              >
                استكشف المحتوى التعليمي
              </Link>
            </div>
          )}

        </div>
      </div>

      {/* Fixed Bottom Navigation - Mobile Only */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-50 safe-area-bottom">
        <div className="grid grid-cols-3 gap-1 px-2 py-1">
          <Link
            to="/notifications"
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors relative"
          >
            <i className="fas fa-bell text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">الإشعارات</span>
            {unreadCount > 0 && (
              <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </Link>
          
          <Link
            to={ROUTES.DASHBOARD}
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg bg-primary text-white transition-colors"
          >
            <i className="fas fa-home text-lg mb-0.5"></i>
            <span className="text-[9px] font-bold">لوحة التحكم</span>
          </Link>
          
          <Link
            to="/profile"
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <i className="fas fa-user text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">الملف الشخصي</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
